import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  TextField,
  Button,
  Typography,
  Box
} from '@mui/material';
import { toast } from 'react-toastify';
import { useAuth } from '../hooks/useAuth';
import healthcheckService from '../services/healthcheckService';
import SocialLoginButtons from '../components/SocialLoginButtons';
import '../styles/pages/Login.scss';

interface CadastroFormData {
  full_name: string;
  cpf: string;
  phone: string;
  email: string;
  password: string;
  confirm_password: string;
  birth_date: string;
}

const Cadastro: React.FC = () => {
  const navigate = useNavigate();
  const { register: registerUser } = useAuth();
  const [loading, setLoading] = useState<boolean>(false);
  const [healthcheckLoading, setHealthcheckLoading] = useState<boolean>(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch
  } = useForm<CadastroFormData>();

  const password = watch('password');

  const handleHealthcheck = async (): Promise<void> => {
    setHealthcheckLoading(true);
    try {
      const response = await healthcheckService.checkHealth();
      toast.success(`Status da API: ${response.status}`);
    } catch (error: any) {
      toast.error('Erro ao verificar status da API');
      console.error('Healthcheck error:', error);
    } finally {
      setHealthcheckLoading(false);
    }
  };

  const onSubmit = async (data: CadastroFormData): Promise<void> => {
    setLoading(true);
    try {
      const cadastroData = {
        full_name: data.full_name,
        cpf: data.cpf,
        phone: data.phone,
        email: data.email,
        password: data.password,
        confirm_password: data.confirm_password,
        birth_date: data.birth_date
      };
      
      await registerUser(cadastroData);
      toast.success('Cadastro realizado com sucesso!');
      // Redirecionar para completar perfil
      navigate('/inicio/completar-perfil');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao realizar cadastro');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <Typography variant="h4" component="h1" gutterBottom>
        Criar conta na plataforma-hml
      </Typography>
      
      <Box component="form" onSubmit={handleSubmit(onSubmit)} className="login-form">
        <TextField
          {...register('full_name', { 
            required: 'Por favor digite seu nome completo',
            minLength: {
              value: 3,
              message: 'O nome deve ter pelo menos 3 caracteres'
            }
          })}
          label="Nome completo"
          placeholder="Digite seu nome completo"
          error={!!errors.full_name}
          helperText={errors.full_name?.message}
          fullWidth
          margin="normal"
          variant="outlined"
        />

        <TextField
          {...register('cpf', { 
            required: 'Por favor digite seu CPF',
            pattern: {
              value: /^\d{3}\.\d{3}\.\d{3}-\d{2}$/,
              message: 'Digite um CPF válido (formato: 123.456.789-00)'
            }
          })}
          label="CPF"
          placeholder="123.456.789-00"
          error={!!errors.cpf}
          helperText={errors.cpf?.message}
          fullWidth
          margin="normal"
          variant="outlined"
        />

        <TextField
          {...register('phone', { 
            required: 'Por favor digite seu telefone',
            pattern: {
              value: /^\(\d{2}\) \d{5}-\d{4}$/,
              message: 'Digite um telefone válido (formato: (11) 99999-9999)'
            }
          })}
          label="Telefone"
          placeholder="(11) 99999-9999"
          error={!!errors.phone}
          helperText={errors.phone?.message}
          fullWidth
          margin="normal"
          variant="outlined"
        />

        <TextField
          {...register('email', { 
            required: 'Por favor digite seu e-mail',
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: 'Digite um e-mail válido'
            }
          })}
          label="E-mail"
          type="email"
          placeholder="Seu e-mail"
          error={!!errors.email}
          helperText={errors.email?.message}
          fullWidth
          margin="normal"
          variant="outlined"
        />

        <TextField
          {...register('birth_date', { 
            required: 'Por favor digite sua data de nascimento'
          })}
          label="Data de nascimento"
          type="date"
          error={!!errors.birth_date}
          helperText={errors.birth_date?.message}
          fullWidth
          margin="normal"
          variant="outlined"
          InputLabelProps={{
            shrink: true,
          }}
        />

        <TextField
          {...register('password', { 
            required: 'Digite sua senha',
            minLength: {
              value: 6,
              message: 'A senha deve ter pelo menos 6 caracteres'
            }
          })}
          label="Senha"
          type="password"
          placeholder="Digite sua senha"
          error={!!errors.password}
          helperText={errors.password?.message}
          fullWidth
          margin="normal"
          variant="outlined"
        />

        <TextField
          {...register('confirm_password', { 
            required: 'Confirme sua senha',
            validate: (value: string) => value === password || 'As senhas não coincidem'
          })}
          label="Confirmar senha"
          type="password"
          placeholder="Confirme sua senha"
          error={!!errors.confirm_password}
          helperText={errors.confirm_password?.message}
          fullWidth
          margin="normal"
          variant="outlined"
        />

        <Button
          type="submit"
          variant="contained"
          color="primary"
          fullWidth
          size="large"
          disabled={loading}
          className="btn-entrar"
        >
          {loading ? 'Cadastrando...' : 'Cadastrar'}
        </Button>

        <Button
          type="button"
          variant="outlined"
          color="secondary"
          fullWidth
          size="large"
          disabled={healthcheckLoading}
          onClick={handleHealthcheck}
          style={{ marginTop: '16px' }}
        >
          {healthcheckLoading ? 'Verificando...' : 'Verificar Status da API'}
        </Button>

        <SocialLoginButtons 
          variant="signup"
          disabled={loading || healthcheckLoading}
        />

        <Typography variant="body2" align="center" className="cadastro-text">
          já tem uma conta?
        </Typography>

        <Button
          component={Link}
          to="/inicio/login"
          variant="contained"
          color="secondary"
          fullWidth
          size="large"
        >
          clique aqui e faça login
        </Button>
      </Box>
    </div>
  );
};

export default Cadastro; 